import { CartModel } from "@insite/client-framework/Types/ApiModels";

interface AddedItem {
    productId?: string;
    qtyOrdered?: number;
}

/**
 * Computes the optimistic increment to apply to the mini-cart badge when an add-to-cart
 * succeeds, matching the "Mini-Cart Quantity Display" setting (Line Item Count vs
 * Total Item Quantity).
 *
 * That setting is backend-only (it drives how the server fills CartModel.totalCountDisplay)
 * and isn't exposed to the storefront, so we infer the mode from the current cart: if
 * totalCountDisplay tracks lineCount it's "Line Item Count"; otherwise it's
 * "Total Item Quantity". When the two coincide (empty cart, or every line has qty 1) the
 * mode is ambiguous and we default to total quantity — the authoritative cart reload
 * reconciles the badge within ~1s either way.
 *
 * Note: in Line Item Count mode this counts every distinct product being added as a new
 * line; re-adding a product already in the cart will briefly overshoot by 1 until reload.
 */
export const getOptimisticCartDelta = (cart: CartModel | undefined, addedItems: AddedItem[]): number => {
    const totalQuantity = addedItems.reduce((sum, item) => sum + (item.qtyOrdered || 0), 0);

    const isLineItemCountMode =
        !!cart && cart.totalCountDisplay === cart.lineCount && cart.totalCountDisplay !== cart.totalQtyOrdered;

    if (isLineItemCountMode) {
        return new Set(addedItems.map(item => item.productId).filter(Boolean)).size;
    }

    return totalQuantity;
};
